import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";
import { ApiRequestError } from "../../../shared/api/client.ts";
import { useConfirmPasswordReset, useRequestPasswordReset } from "../api/session.ts";
import {
  ForgotPasswordScreen,
  forgotPasswordLinkClass,
} from "../components/forgot-password-screen.tsx";
import { forgotPasswordMessages } from "../messages.ts";
import { describeError, isPasswordChangedFailure } from "../model/error-message.ts";

/** The server refused the new password as common or found in breaches (not for its length). */
function isCommonPasswordRefusal(error: unknown): boolean {
  if (!(error instanceof ApiRequestError) || error.code !== "weak_password") return false;
  const rule = error.details?.rule;
  return rule === "common_password" || rule === "compromised_password";
}

/**
 * `/forgot-password` (flag iam.password-reset; no environment parameter, design D12): asks for a
 * reset code, then sets the new password with it and sends the user to the sign-in screen (also
 * when the answer is a `503` that says the new password is set). No session starts here.
 */
export function ForgotPasswordPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const request = useRequestPasswordReset();
  const confirm = useConfirmPasswordReset();
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [newCodeRequested, setNewCodeRequested] = useState(false);

  const backLink = (
    <Link to="/login" className={forgotPasswordLinkClass}>
      {intl.formatMessage(forgotPasswordMessages.backToLogin)}
    </Link>
  );

  if (email === undefined) {
    return (
      <ForgotPasswordScreen
        step="request"
        pending={request.isPending}
        error={request.error ? describeError(intl, request.error) : undefined}
        backLink={backLink}
        onRequest={(value) => {
          request.mutate(
            { email: value },
            {
              onSuccess: () => {
                setEmail(value);
              },
            },
          );
        }}
      />
    );
  }

  const failure = confirm.error ?? request.error;
  return (
    <ForgotPasswordScreen
      step="confirm"
      email={email}
      pending={confirm.isPending || request.isPending}
      error={failure ? describeError(intl, failure) : undefined}
      newCodeRequested={newCodeRequested}
      commonRefused={isCommonPasswordRefusal(confirm.error)}
      backLink={backLink}
      onConfirm={({ code, newPassword }) => {
        request.reset();
        confirm.mutate(
          { email, code, new_password: newPassword },
          {
            onSuccess: () => {
              void navigate({ to: "/login", search: { reason: "password_reset" } });
            },
            // the new password is set even though the reset could not be finished: the code is
            // used up, so the user signs in with the new password instead of trying again here
            onError: (error) => {
              if (isPasswordChangedFailure(error))
                void navigate({ to: "/login", search: { reason: "password_reset_unfinished" } });
            },
          },
        );
      }}
      onNewCode={() => {
        confirm.reset();
        request.mutate(
          { email },
          {
            onSuccess: () => {
              setNewCodeRequested(true);
            },
          },
        );
      }}
      onOtherEmail={() => {
        confirm.reset();
        request.reset();
        setNewCodeRequested(false);
        setEmail(undefined);
      }}
    />
  );
}
